import { useState, useCallback, useMemo } from "react";
import { useCurrentRapidData } from "@rapid/store";
import { projectSelector } from "@rflocal/shared";

/**
 * Shared hook for sharing workflow URLs
 * Generates the shareable URL and handles copying to clipboard with feedback
 *
 * @returns An object containing the share URL, copied state, and handleShare callback
 *
 * @example
 * ```tsx
 * const { shareUrl, copied, handleShare } = useShareWorkflow();
 *
 * <button onClick={handleShare} disabled={!shareUrl}>
 *   {copied ? "Link Copied!" : "Share"}
 * </button>
 * ```
 */
export function useShareWorkflow() {
    const rapidData = useCurrentRapidData();
    const [copied, setCopied] = useState(false);

    // Generate shareable URL from rapid data
    const shareUrl = useMemo(() => {
        if (!rapidData?.url) {
            return null;
        }
        return `${projectSelector.rapidUrl()}/p/${rapidData.url}`;
    }, [rapidData]);

    // Handle copying URL to clipboard
    const handleShare = useCallback(async () => {
        if (!shareUrl) {
            console.error("No shareable URL available");
            return;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            // Reset after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    }, [shareUrl]);

    return { shareUrl, copied, handleShare };
}
